import { Handle, Position, useNodeConnections } from '@xyflow/react'
import { isFired } from './useFiredFlash'

/**
 * Wayfinding / movement nodes (ENG-592) — Unreal-style AI movement issued from
 * the Event Graph. Each action node calls movementComponent (src/lib/
 * movementComponent.js), which path-finds over the loaded navmesh and walks the
 * avatar. Completion is reported by the OnMoveCompleted event node.
 *
 * Action nodes are blue (movement); the event node is orange like other events.
 */

const ACCENT = '#5aa0ff'
const HEADER_BG = '#0a1c3a'
const HEADER_ACTIVE = '#10305a'
const BORDER_SEL = '#5aa0ff'
const BORDER_IDLE = '#0d2440'

const execStyle = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }
const numStyle = {
  width: '100%', boxSizing: 'border-box', background: '#040c1a', border: '1px solid #14304f',
  color: '#8ac0ff', padding: '3px 6px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)',
  outline: 'none', textAlign: 'right',
}
const stop = e => e.stopPropagation()

function Shell({ selected, fired, minWidth = 220, icon, label, tip, children }) {
  return (
    <div title={tip} style={{
      background: selected ? '#08152a' : '#050b16',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_SEL : BORDER_IDLE}`,
      borderRadius: 6, minWidth, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn" style={execStyle} />
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>{label}</span>
        <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />
      </div>
      {children}
    </div>
  )
}

function NumField({ label, pinId, value, onChange }) {
  return (
    <div className="nodrag nowheel"
      style={{ padding: '4px 12px', position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}
      onPointerDown={stop} onMouseDown={stop} onClick={stop}>
      {pinId && (
        <Handle type="target" position={Position.Left} id={pinId}
          style={{ background: '#60a0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60a0d044' }} />
      )}
      <span style={{ color: '#3a6090', fontSize: 10, minWidth: 64 }}>{label}</span>
      <input type="number" step={0.1} value={value}
        onChange={e => { const v = parseFloat(e.target.value); onChange(Number.isFinite(v) ? v : 0) }}
        onPointerDown={stop} onMouseDown={stop} onClick={stop} onKeyDown={stop} onWheel={stop}
        style={numStyle} />
    </div>
  )
}

// Unreal-style horizontal vector input: a single vector pin + inline X/Y/Z
// boxes. Following the Blueprint convention — when the pin is WIRED the inline
// editor collapses (the wire drives it); when unconnected you type values.
// `nodeId` is the owning node's id (for connection lookup); `onChange(field, v)`
// writes a value back. The runtime prefers the wired value (see EventRuntime).
export function VectorField({ nodeId, label, pinId, x, y, z, fields = ['x', 'y', 'z'], onChange }) {
  const connections = useNodeConnections({ id: nodeId, handleType: 'target', handleId: pinId })
  const wired = !!pinId && connections.length > 0
  const cell = {
    ...numStyle, width: 44, padding: '3px 4px', textAlign: 'center', MozAppearance: 'textfield',
  }
  const vals = [x ?? 0, y ?? 0, z ?? 0]
  const axes = ['X', 'Y', 'Z']
  return (
    <div className="nodrag nowheel"
      style={{ padding: '4px 12px', position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}
      onPointerDown={stop} onMouseDown={stop} onClick={stop}>
      {pinId && (
        <Handle type="target" position={Position.Left} id={pinId}
          style={{ background: '#60a0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60a0d044' }} />
      )}
      <span style={{ color: '#3a6090', fontSize: 10, minWidth: 44 }}>{label}</span>
      {wired ? (
        <span style={{ color: '#5aa0ff', fontSize: 10, fontStyle: 'italic' }}>← vector</span>
      ) : (
        fields.map((field, i) => (
          <input key={field} type="number" step={0.1} value={vals[i]} title={axes[i]}
            onChange={e => { const v = parseFloat(e.target.value); onChange(field, Number.isFinite(v) ? v : 0) }}
            onPointerDown={stop} onMouseDown={stop} onClick={stop} onKeyDown={stop} onWheel={stop}
            style={cell} />
        ))
      )}
    </div>
  )
}

export function MoveToLocationNode({ id, data, selected }) {
  return (
    <Shell selected={selected} fired={isFired(data)} minWidth={236} icon="🧭" label="Move To Location"
      tip="Walk to a world point on the navmesh. Wire in-location with a vector, or type X/Y/Z.">
      <VectorField nodeId={id} label="Location" pinId="in-location"
        x={data.x} y={data.y} z={data.z} fields={['x', 'y', 'z']}
        onChange={(field, v) => data.onChange?.(field, v)} />
      <NumField label="Accept R" value={data.acceptanceRadius ?? 0.2} onChange={v => data.onChange?.('acceptanceRadius', v)} />
    </Shell>
  )
}

export function MoveToActorNode({ data, selected }) {
  return (
    <Shell selected={selected} fired={isFired(data)} icon="🎯" label="Move To Actor"
      tip="Follow a named scene object / marker. Wire in-target, or type a name.">
      <div style={{ padding: '8px 12px 4px', position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Handle type="target" position={Position.Left} id="in-target"
          style={{ background: '#c060e8', width: 10, height: 10, borderRadius: '50%', border: '2px solid #80309866' }} />
        <input value={data.target ?? ''} onChange={e => data.onChange?.('target', e.target.value)}
          onMouseDown={stop} placeholder="object / marker name"
          style={{ ...numStyle, textAlign: 'left', color: '#c79cf0' }} />
      </div>
      <NumField label="Accept R" value={data.acceptanceRadius ?? 0.2} onChange={v => data.onChange?.('acceptanceRadius', v)} />
    </Shell>
  )
}

export function MoveToRandomPointNode({ id, data, selected }) {
  return (
    <Shell selected={selected} fired={isFired(data)} minWidth={236} icon="🎲" label="Move To Random Point"
      tip="Walk to a random reachable navmesh point within radius of the center. Wire in-center with a vector, or type X/Y/Z. (UE GetRandomReachablePointInRadius)">
      <VectorField nodeId={id} label="Center" pinId="in-center"
        x={data.centerX} y={data.centerY} z={data.centerZ}
        fields={['centerX', 'centerY', 'centerZ']}
        onChange={(field, v) => data.onChange?.(field, v)} />
      <NumField label="Radius" pinId="in-radius" value={data.radius ?? 3} onChange={v => data.onChange?.('radius', v)} />
    </Shell>
  )
}

export function AbortMovementNode({ data, selected }) {
  return (
    <Shell selected={selected} fired={isFired(data)} minWidth={200} icon="⛔" label="Abort Movement"
      tip="Stop any active move (UE StopMovement).">
      <div style={{ height: 4 }} />
    </Shell>
  )
}

// Event node — fires when a move ends. Orange like other events; exposes the
// `result` field (success | failed | aborted) on out-result.
export function OnMoveCompletedNode({ data, selected }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? '#2a1405' : '#1a0d03',
      border: `2px solid ${fired ? '#ffcc44' : selected ? '#ff7040' : '#5a3010'}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{ background: selected ? '#5a3010' : '#3a2008', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
        <span style={{ fontSize: 13 }}>🏁</span>
        <span style={{ color: '#ff9050', fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>On Move Completed</span>
        <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />
      </div>
      <div style={{ padding: '6px 12px 8px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative', justifyContent: 'flex-end' }}>
        <span style={{ color: '#6a4520', fontSize: 9 }}>result (success|failed|aborted)</span>
        <Handle type="source" position={Position.Right} id="out-result"
          style={{ background: '#c060e8', width: 10, height: 10, borderRadius: '50%', border: '2px solid #80309866' }} />
      </div>
    </div>
  )
}
